import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Send, X, Minimize2, Eye, Volume2, VolumeX, Sparkles, ArrowRight, Mail, Building2, Phone, Loader2 } from "lucide-react";
import axios from "axios";
import { SavingsReport } from "./SavingsReport";
import { InChatQuiz } from "./InChatQuiz";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShieldIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    <path d="M40 8L14 22V44C14 59 25.5 72 40 76C54.5 72 66 59 66 44V22L40 8Z" stroke="#D4AF37" strokeWidth="2.5" fill="rgba(212,175,55,0.08)" />
    <path d="M26 44C26 44 32 35 40 35C48 35 54 44 54 44C54 44 48 53 40 53C32 53 26 44 26 44Z" stroke="#00FF41" strokeWidth="1.5" fill="none" opacity="0.8" />
    <circle cx="40" cy="44" r="5" stroke="#00FF41" strokeWidth="1.5" fill="rgba(0,255,65,0.15)" />
    <circle cx="40" cy="44" r="2.5" fill="#00FF41" />
  </svg>
);

const TypingIndicator = () => (
  <div className="flex items-center gap-1 px-4 py-3">
    <div className="flex gap-1">
      {[0, 1, 2].map((i) => (
        <motion.div key={i} className="w-2 h-2 rounded-full bg-gold"
          animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1, 0.8] }}
          transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }} />
      ))}
    </div>
    <span className="text-xs text-slate-500 ml-2">Argus is thinking...</span>
  </div>
);

// Email Collection Form Component
const EmailCollectionForm = ({ onSubmit, isLoading }) => {
  const [email, setEmail] = useState("");
  const [restaurantName, setRestaurantName] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!email) newErrors.email = "Email is required";
    else if (!validateEmail(email)) newErrors.email = "Please enter a valid email";
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onSubmit({ email, restaurantName, phone });
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={handleSubmit}
      className="bg-gradient-to-br from-[#0A1244] to-[#060D3A] rounded-xl border border-gold/30 p-4 my-3"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-gold" />
        <p className="text-sm font-medium text-white">Get Your Personalized Savings Report</p>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        I'll analyze our conversation and show you exactly what Argus Xenia could save your restaurant.
      </p>
      
      <div className="space-y-3">
        <div>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErrors({}); }}
              placeholder="Your email *"
              className="pl-10 bg-indigo-700/30 border-gold/20 text-white placeholder:text-slate-500 focus:border-gold text-sm"
              data-testid="email-collection-input"
            />
          </div>
          {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
        </div>
        
        <div className="relative">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            type="text"
            value={restaurantName}
            onChange={(e) => setRestaurantName(e.target.value)}
            placeholder="Restaurant name (optional)"
            className="pl-10 bg-indigo-700/30 border-gold/20 text-white placeholder:text-slate-500 focus:border-gold text-sm"
          />
        </div>
        
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone (optional, for trial reminders)"
            className="pl-10 bg-indigo-700/30 border-gold/20 text-white placeholder:text-slate-500 focus:border-gold text-sm"
          />
        </div>
        
        <Button
          type="submit"
          disabled={isLoading}
          className="w-full bg-gold hover:bg-gold/90 text-indigo-700 font-inter font-bold py-2.5 rounded-lg"
          data-testid="generate-report-btn"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating Report...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Generate My Savings Report
            </>
          )}
        </Button>
      </div>
      
      <p className="text-[10px] text-slate-500 text-center mt-3">
        We respect your privacy. No spam, ever.
      </p>
    </motion.form>
  );
};

export const ChatWidget = ({
  agentType = "argus",
  title = "Talk to Argus",
  autoOpen = false,
  autoOpenDelay = 3000,
  idleAutoOpen = false,
  idleTimeout = 60000,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId] = useState(() => `${agentType}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [initialized, setInitialized] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(null);
  const [sandboxOffered, setSandboxOffered] = useState(false);
  const [sandboxStarting, setSandboxStarting] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  
  // Quiz flow state
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [quizOffered, setQuizOffered] = useState(false);
  
  // Savings report flow state
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailCollected, setEmailCollected] = useState(false);
  const [savingsReport, setSavingsReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [restaurantName, setRestaurantName] = useState("");
  
  const audioRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages, isTyping, showQuiz]);

  // Auto-open logic
  useEffect(() => {
    if (hasAutoOpened) return;
    if (autoOpen) {
      const timer = setTimeout(() => { setIsOpen(true); setHasAutoOpened(true); }, autoOpenDelay);
      return () => clearTimeout(timer);
    }
  }, [autoOpen, autoOpenDelay, hasAutoOpened]);

  // Idle auto-open for landing page
  useEffect(() => {
    if (!idleAutoOpen || hasAutoOpened) return;
    let idleTimer;
    const resetTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => { setIsOpen(true); setHasAutoOpened(true); }, idleTimeout);
    };
    resetTimer();
    const events = ["mousemove", "click", "scroll", "keypress"];
    events.forEach(e => window.addEventListener(e, resetTimer));
    return () => {
      clearTimeout(idleTimer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [idleAutoOpen, idleTimeout, hasAutoOpened]);

  // Initialize chat with AI greeting
  const initChat = useCallback(async () => {
    if (initialized) return;
    setInitialized(true);
    setIsTyping(true);
    // Track chat open in Google Analytics
    if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
      window.gtag('event', 'chat_opened', {
        'event_category': 'engagement',
        'event_label': agentType
      });
    }
    try {
      const res = await axios.post(`${API}/chat/init`, {
        session_id: sessionId,
        message: "",
        agent_type: agentType,
        context: quizData,
      });
      setMessages([{ role: "assistant", content: res.data.response, timestamp: new Date().toISOString() }]);
    } catch (err) {
      const fallback = agentType === "founder"
        ? "Hey, I'm Argus Founder. Welcome to the Founding 50. What's the biggest operational fire you'd love to put out?"
        : "Hey — I'm Argus, the intelligence behind Argus Xenia. I help restaurants understand how the platform can support their operation. What kind of restaurant are you running?";
      setMessages([{ role: "assistant", content: fallback, timestamp: new Date().toISOString() }]);
    } finally {
      setIsTyping(false);
    }
  }, [initialized, sessionId, agentType, quizData]);

  useEffect(() => {
    if (isOpen && !initialized) {
      initChat();
    }
  }, [isOpen, initialized, initChat]);

  // Helper function to strip markdown formatting from AI responses
  const stripMarkdown = (text) => {
    return text
      .replace(/\*\*([^*]+)\*\*/g, '$1')  // Remove **bold**
      .replace(/\*([^*]+)\*/g, '$1')       // Remove *italic*
      .replace(/`([^`]+)`/g, '$1')         // Remove `code`
      .replace(/^[-*]\s+/gm, '• ')         // Convert list markers to bullet
      .replace(/^#+\s+/gm, '')             // Remove headers
      .replace(/\n---\n/g, '\n\n');        // Remove horizontal rules
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isTyping) return;

    const userMsg = { role: "user", content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsTyping(true);
    
    // Count user messages
    const currentUserMessageCount = messages.filter(m => m.role === "user").length + 1;
    setMessageCount(currentUserMessageCount);
    
    const textLower = text.toLowerCase().trim();
    
    // DETERMINISTIC QUIZ TRIGGER: If user explicitly asks for quiz, show it immediately
    const isExplicitQuizRequest = (
      textLower.includes("quiz") ||
      textLower.includes("assessment") ||
      textLower === "take it" ||
      textLower === "do it" ||
      textLower === "lets do it" ||
      textLower === "let's do it"
    );
    
    if (isExplicitQuizRequest && !showQuiz && !quizData) {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "You got it — here's the assessment.",
        timestamp: new Date().toISOString()
      }]);
      setShowQuiz(true);
      setQuizOffered(true);
      setIsTyping(false);
      setTimeout(() => inputRef.current?.focus(), 100);
      return;
    }

    try {
      const res = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: text,
        agent_type: agentType,
        context: quizData,
      });
      
      // Strip markdown from AI response
      let responseText = stripMarkdown(res.data.response);
      
      // Get action marker from backend response
      const actionMarker = res.data.action;
      
      // FALLBACK: Check if AI response mentions quiz should be showing but no marker
      const aiMentionsQuiz = responseText.toLowerCase().includes("quiz should be") || 
                            responseText.toLowerCase().includes("assessment should be") ||
                            responseText.toLowerCase().includes("here's the quiz") ||
                            responseText.toLowerCase().includes("let me get that");
      
      // Add the AI response
      const assistantMsg = { 
        role: "assistant", 
        content: responseText, 
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMsg]);
      
      // Handle action markers from AI OR fallback detection
      const shouldShowQuiz = actionMarker === "SHOW_QUIZ" || (aiMentionsQuiz && !showQuiz && !quizData);
      
      if (shouldShowQuiz) {
        console.log("Showing quiz - marker:", actionMarker, "fallback:", aiMentionsQuiz);
        setShowQuiz(true);
        setQuizOffered(true);
      } else if (actionMarker === "START_SANDBOX") {
        handleStartTrial();
      } else if (actionMarker === "END_CONVERSATION") {
        console.log("Conversation ended naturally");
      }
      
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "I'm having a moment — try again in a sec.", timestamp: new Date().toISOString() }]);
    } finally {
      setIsTyping(false);
      // Refocus input after message sent
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };
  
  // Handle quiz completion
  const handleQuizComplete = async (data) => {
    setQuizData(data);
    setShowQuiz(false);
    setUserEmail(data.email || "");
    setRestaurantName(data.restaurant_name || "");
    
    // Add message showing quiz completed
    setMessages(prev => [...prev, {
      role: "user",
      content: `[Completed assessment${data.restaurant_name ? ` for ${data.restaurant_name}` : ''}]`,
      timestamp: new Date().toISOString(),
      isFormSubmission: true
    }]);
    
    // Generate savings report with quiz data
    if (data.email) {
      setReportLoading(true);
      try {
        const res = await axios.post(`${API}/savings-report/generate`, {
          session_id: sessionId,
          email: data.email,
          restaurant_name: data.restaurant_name || undefined,
          quiz_data: data, // Pass all quiz answers
        });
        
        setSavingsReport(res.data);
        setEmailCollected(true);
        
        // Build a natural summary based on quiz data
        const painPoints = data.pain_points || [];
        const hasCameras = data.cameras && !data.cameras.some(c => c.includes("No cameras"));
        
        // Determine their quick wins based on pain points
        let quickWins = [];
        if (painPoints.some(p => p.toLowerCase().includes("call") || p.toLowerCase().includes("phone"))) {
          quickWins.push("Echo for the phone problem");
        }
        if (painPoints.some(p => p.toLowerCase().includes("training") || p.toLowerCase().includes("staff") || p.toLowerCase().includes("turnover"))) {
          quickWins.push("Athena for the training chaos");
        }
        if (painPoints.some(p => p.toLowerCase().includes("waste") || p.toLowerCase().includes("cost") || p.toLowerCase().includes("consistency"))) {
          quickWins.push(hasCameras ? "Apollo since you've got cameras" : "Apollo when you add cameras");
        }
        if (painPoints.some(p => p.toLowerCase().includes("communication") || p.toLowerCase().includes("kitchen"))) {
          quickWins.push("Iris for the communication gaps");
        }
        
        // Build natural response with savings estimate - NO BUTTONS, just verbal
        let summaryText = `I've sent the full breakdown to ${data.email} — check your inbox (or spam). `;
        summaryText += `Based on your setup, we're looking at around $${res.data.monthly_total.toLocaleString()}/month in potential savings. `;
        
        if (quickWins.length > 0) {
          summaryText += `Your quick wins would be ${quickWins.slice(0, 2).join(" and ")}. `;
        }
        
        if (!hasCameras) {
          summaryText += "You don't have cameras yet, so some of the vision stuff is future potential — but there's plenty you can start with today. ";
        }
        
        // Verbally ask about sandbox - NO BUTTON
        summaryText += "\n\nWant to jump into a free trial and actually test some of this out?";
        
        // Add message with natural summary - removed offerSandbox flag (no button)
        setMessages(prev => [...prev, {
          role: "assistant",
          content: summaryText,
          timestamp: new Date().toISOString(),
          reportSent: true,
          savingsReportData: res.data
        }]);
        
      } catch (err) {
        console.error("Failed to generate savings report:", err);
        setMessages(prev => [...prev, {
          role: "assistant",
          content: `Got your info. I had some trouble generating the report, but based on what you told me I can walk you through the wins. What do you want to dig into first — phone coverage, training systems, or kitchen monitoring?`,
          timestamp: new Date().toISOString()
        }]);
      } finally {
        setReportLoading(false);
      }
    } else {
      // No email provided, continue conversation
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Got it. Based on your setup, I have a pretty good picture now. Want me to walk you through where Argus could help, or is there something specific you want to dig into?",
        timestamp: new Date().toISOString()
      }]);
    }
  };
  
  // Handle quiz cancel
  const handleQuizCancel = () => {
    setShowQuiz(false);
    setMessages(prev => [...prev, {
      role: "assistant",
      content: "No worries, we can skip that. What else would you like to know?",
      timestamp: new Date().toISOString()
    }]);
  };

  // Handle email form submission and generate savings report
  const handleEmailSubmit = async ({ email, restaurantName: restName, phone }) => {
    setReportLoading(true);
    setUserEmail(email);
    setRestaurantName(restName);
    
    // Add a user message showing they submitted
    setMessages(prev => [...prev, {
      role: "user",
      content: `[Submitted: ${email}${restName ? `, ${restName}` : ''}]`,
      timestamp: new Date().toISOString(),
      isFormSubmission: true
    }]);
    
    try {
      const res = await axios.post(`${API}/savings-report/generate`, {
        session_id: sessionId,
        email,
        restaurant_name: restName || undefined,
        phone: phone || undefined,
        notification_preference: phone ? "both" : "email"
      });
      
      setSavingsReport(res.data);
      setEmailCollected(true);
      setShowEmailForm(false);
      
      // Add an assistant message with the report
      setMessages(prev => [...prev, {
        role: "assistant",
        content: `Great news! Based on our conversation, I've calculated your personalized Argus Xenia Savings Report. Take a look:`,
        timestamp: new Date().toISOString(),
        hasSavingsReport: true,
        savingsReportData: res.data
      }]);
      
      // Track in Google Analytics
      if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
        window.gtag('event', 'savings_report_generated', {
          'event_category': 'conversion',
          'event_label': email,
          'value': res.data.monthly_total
        });
      }
      
    } catch (err) {
      console.error("Failed to generate savings report:", err);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "I had trouble generating your report. Let me try a different approach — tell me more about your biggest operational challenge and I'll put together some numbers.",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setReportLoading(false);
    }
  };

  // Start sandbox trial from savings report - redirect to trial registration
  const handleStartTrial = async () => {
    setSandboxStarting(true);
    
    // Track in Google Analytics
    if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
      window.gtag('event', 'trial_cta_clicked', {
        'event_category': 'conversion',
        'event_label': userEmail
      });
    }
    
    // Add confirmation message
    setMessages(prev => [...prev, {
      role: "assistant",
      content: `**Ready to explore Argus Xenia?** I'm taking you to set up your trial account.\n\nYou'll get access to:\n• **Supervisor Echo** - Call our AI receptionist\n• **Supervisor Apollo** - Upload kitchen photos for analysis\n• **Supervisor Athena** - Record voice SOPs\n\nJust create a password and you're in!`,
      timestamp: new Date().toISOString()
    }]);
    
    // Build URL with pre-filled data
    const params = new URLSearchParams();
    if (userEmail) params.set("email", userEmail);
    if (restaurantName) params.set("restaurant", restaurantName);
    if (sessionId) params.set("session", sessionId);
    if (savingsReport?.report_id) params.set("report", savingsReport.report_id);
    
    // Redirect to trial registration after a short delay
    setTimeout(() => {
      window.location.href = `/trial/register?${params.toString()}`;
    }, 1500);
  };

  // Start sandbox session and redirect to SOP Builder (legacy)
  const handleStartSandbox = async () => {
    setSandboxStarting(true);
    try {
      // Get user email from collected state if available
      const email = userEmail || null;
      const name = restaurantName || null;
      
      const res = await axios.post(`${API}/sandbox/init`, {
        email,
        name,
        from_chat_session: sessionId
      });
      
      const { guest_token, session_id } = res.data;
      
      // Store guest token in session storage
      sessionStorage.setItem("argus_guest_token", guest_token);
      sessionStorage.setItem("argus_guest_session", session_id);
      
      // Add confirmation message
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Sandbox activated! Taking you to the SOP Builder now. Press the mic button and tell me a recipe — try to get a temperature wrong, I'll catch it. See you there!",
        timestamp: new Date().toISOString()
      }]);
      
      // Redirect to SOP Builder after a short delay
      setTimeout(() => {
        window.location.href = "/dashboard/sop-builder?sandbox=true";
      }, 1500);
      
    } catch (err) {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Hmm, had trouble setting up the sandbox. Try refreshing the page or let me know your biggest challenge first.",
        timestamp: new Date().toISOString()
      }]);
      setSandboxStarting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // TTS "Listen" functionality
  const handleListen = async (messageIndex, text) => {
    // If already playing this message, stop it
    if (playingAudio === messageIndex) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingAudio(null);
      return;
    }

    setLoadingAudio(messageIndex);
    try {
      const res = await axios.post(`${API}/tts/generate`, {
        text,
        session_id: sessionId,
        message_index: messageIndex,
      });

      if (res.data.mocked) {
        // TTS is mocked - show visual feedback
        setPlayingAudio(messageIndex);
        setLoadingAudio(null);
        // Simulate audio duration based on text length
        const duration = Math.min(text.length * 50, 10000);
        setTimeout(() => setPlayingAudio(null), duration);
      } else if (res.data.audio_data) {
        // Real audio - play the base64 encoded audio
        setPlayingAudio(messageIndex);
        setLoadingAudio(null);
        
        // Create audio from base64 data
        const audioBlob = new Blob(
          [Uint8Array.from(atob(res.data.audio_data), c => c.charCodeAt(0))],
          { type: 'audio/mpeg' }
        );
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        
        audio.onended = () => {
          setPlayingAudio(null);
          URL.revokeObjectURL(audioUrl);
        };
        audio.onerror = () => {
          setPlayingAudio(null);
          URL.revokeObjectURL(audioUrl);
        };
        
        audio.play().catch(() => setPlayingAudio(null));
      }
    } catch {
      setLoadingAudio(null);
    }
  };

  return (
    <>
      {/* Launcher Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            data-testid="chat-launcher"
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 z-[60] flex items-center gap-3 bg-[#040F49] border-2 border-gold/40 rounded-full pl-3 pr-5 py-3 shadow-2xl group hover:border-gold/70 transition-colors"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.05 }}
            style={{ boxShadow: "0 0 25px rgba(212,175,55,0.2)" }}
          >
            <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center animate-shield-pulse">
              <ShieldIcon size={24} />
            </div>
            <span className="text-sm font-inter font-bold text-gold hidden sm:inline">
              {agentType === "founder" ? "Talk to Argus Founder" : "Talk to Argus"}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            data-testid="chat-panel"
            className="fixed z-[60] bottom-0 right-0 sm:bottom-6 sm:right-6 w-full sm:w-[420px] h-[100dvh] sm:h-[600px] sm:max-h-[80vh] flex flex-col bg-[#040F49] border border-gold/20 sm:rounded-xl overflow-hidden shadow-2xl"
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gold/15 bg-[#060D3A]">
              <div className="flex items-center gap-3">
                <div className="animate-shield-pulse">
                  <ShieldIcon size={28} />
                </div>
                <div>
                  <h3 className="font-inter text-sm font-bold text-gold">{title}</h3>
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-lime animate-pulse" />
                    <span className="text-xs text-lime/80 font-mono">Online</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setIsOpen(false)} className="p-1.5 rounded hover:bg-indigo-500/20 text-slate-400 hover:text-white transition-colors" data-testid="chat-minimize">
                  <Minimize2 className="w-4 h-4" />
                </button>
                <button onClick={() => setIsOpen(false)} className="p-1.5 rounded hover:bg-indigo-500/20 text-slate-400 hover:text-white transition-colors" data-testid="chat-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="chat-messages">
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {msg.role === "assistant" && (
                    <div className="mr-2 mt-1 shrink-0">
                      <div className="w-7 h-7 rounded-full bg-gold/10 flex items-center justify-center">
                        <Eye className="w-3.5 h-3.5 text-lime" />
                      </div>
                    </div>
                  )}
                  <div className={`max-w-[85%] rounded-xl px-4 py-3 ${
                    msg.role === "user"
                      ? msg.isFormSubmission 
                        ? "bg-lime/10 text-lime border border-lime/20"
                        : "bg-indigo-500/30 text-white border border-indigo-400/20"
                      : "bg-[#0A1244] text-slate-200 border border-gold/15"
                  }`}>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    
                    {/* Email Collection Form */}
                    {msg.showEmailForm && !emailCollected && (
                      <EmailCollectionForm 
                        onSubmit={handleEmailSubmit} 
                        isLoading={reportLoading} 
                      />
                    )}
                    
                    
                    {/* Savings Report Display */}
                    {msg.hasSavingsReport && msg.savingsReportData && (
                      <SavingsReport
                        reportData={msg.savingsReportData}
                        isLoading={false}
                        onStartTrial={handleStartTrial}
                        trialLoading={sandboxStarting}
                        restaurantName={restaurantName}
                      />
                    )}
                    
                    {/* Legacy Sandbox buttons - kept for backwards compatibility */}
                    {msg.hasSandboxOffer && !sandboxStarting && !showEmailForm && !savingsReport && (
                      <motion.button
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        onClick={handleStartSandbox}
                        className="mt-3 w-full flex items-center justify-center gap-2 bg-gold hover:bg-gold/80 text-indigo-700 font-bold text-sm py-2.5 px-4 rounded-lg transition-all"
                        data-testid="sandbox-cta"
                      >
                        <Sparkles className="w-4 h-4" />
                        Enter the Sandbox
                        <ArrowRight className="w-4 h-4" />
                      </motion.button>
                    )}
                    
                    {msg.hasSandboxOffer && sandboxStarting && (
                      <div className="mt-3 w-full flex items-center justify-center gap-2 bg-gold/50 text-indigo-700 font-bold text-sm py-2.5 px-4 rounded-lg">
                        <div className="w-4 h-4 border-2 border-indigo-700 border-t-transparent rounded-full animate-spin" />
                        Setting up your sandbox...
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between mt-1.5">
                      {/* Listen button for assistant messages */}
                      {msg.role === "assistant" && (
                        <button
                          data-testid={`listen-btn-${i}`}
                          onClick={() => handleListen(i, msg.content)}
                          disabled={loadingAudio === i}
                          className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded transition-colors ${
                            playingAudio === i 
                              ? "bg-gold/20 text-gold" 
                              : "text-slate-500 hover:text-gold hover:bg-gold/10"
                          }`}
                          title="Listen to response"
                        >
                          {loadingAudio === i ? (
                            <span className="animate-pulse">...</span>
                          ) : playingAudio === i ? (
                            <>
                              <VolumeX className="w-3 h-3" />
                              <span>Stop</span>
                            </>
                          ) : (
                            <>
                              <Volume2 className="w-3 h-3" />
                              <span>Listen</span>
                            </>
                          )}
                        </button>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-600">
                          {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                        {msg.role === "user" && (
                          <span className="text-[10px] text-gold/50">seen</span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="mr-2 mt-1 shrink-0">
                    <div className="w-7 h-7 rounded-full bg-gold/10 flex items-center justify-center animate-pulse-gold">
                      <Eye className="w-3.5 h-3.5 text-lime" />
                    </div>
                  </div>
                  <div className="bg-[#0A1244] border border-gold/20 rounded-xl animate-pulse-gold">
                    <TypingIndicator />
                  </div>
                </div>
              )}
              
              {/* In-Chat Quiz */}
              {showQuiz && (
                <InChatQuiz 
                  onComplete={handleQuizComplete}
                  onCancel={handleQuizCancel}
                />
              )}
              
              {/* Report Loading State */}
              {reportLoading && (
                <div className="flex justify-start">
                  <div className="mr-2 mt-1 shrink-0">
                    <div className="w-7 h-7 rounded-full bg-gold/10 flex items-center justify-center">
                      <Loader2 className="w-3.5 h-3.5 text-gold animate-spin" />
                    </div>
                  </div>
                  <div className="bg-[#0A1244] border border-gold/20 rounded-xl px-4 py-3">
                    <p className="text-sm text-slate-400">Crunching your numbers...</p>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="px-4 py-3 border-t border-gold/15 bg-[#060D3A]">
              <div className="flex items-center gap-2">
                <Input
                  ref={inputRef}
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={showQuiz ? "Complete the assessment above..." : isTyping ? "Argus is thinking..." : "Type your message..."}
                  disabled={isTyping || showQuiz}
                  className="flex-1 bg-indigo-700/30 border-gold/15 text-white placeholder:text-slate-500 focus:border-gold text-sm rounded-lg"
                />
                <Button
                  data-testid="chat-send"
                  onClick={sendMessage}
                  disabled={!input.trim() || isTyping || showQuiz}
                  size="icon"
                  className="bg-gold hover:bg-gold-500 text-indigo-700 rounded-lg w-10 h-10 shrink-0 disabled:opacity-30"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-[10px] text-slate-600 mt-2 text-center">
                AI-powered by Argus Xenia — responses may vary
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
